import { useHttpFetch } from '@backmarket/nuxt-module-http-v2/useHttpFetch'
import { useSWRAsyncData } from '@backmarket/nuxt-module-http-v2/useSWRAsyncData'
import { useLogger } from '@backmarket/nuxt-module-logger/useLogger'

import type { Favorite } from '~/api/favorites/types'

import { LOG_ERROR_MESSAGE } from '../Favorites.constants'
import { getErrorType } from '../utils/errorUtils'

/**
 * Fetches the list of favorites for the authenticated user with SWR caching.
 *
 * @param options - Configuration options
 * @param options.onDataUpdated - Callback executed when data is updated
 * @returns Object with data, error, hasFetchedOnce, and other useSWRAsyncData properties
 */
export function useFetchFavorites({
  onDataUpdated,
}: {
  onDataUpdated?: (data: Favorite[] | null | undefined) => void
}) {
  const $httpFetch = useHttpFetch()
  const logger = useLogger()

  return useSWRAsyncData(
    'favorites',
    () =>
      $httpFetch('/my-favorites/favorites', {
        onResponseError: (context) => {
          const statusCode = context.response?.status ?? 0
          const errorType = getErrorType(statusCode)
          const errorMessage = context.response?.statusText || 'Unknown error'

          logger.error(LOG_ERROR_MESSAGE.FETCH, {
            error: new Error(errorMessage),
            feature: 'favorites',
            favorite_action: 'fetch_list',
            api_endpoint: '/my-favorites/favorites',
            cache_strategy: 'swr',
            error_type: errorType,
            error_status: statusCode,
            owners: ['bot-squad-lifecycle-front'],
          })
        },
      }),
    {
      onDataUpdated,
    },
  )
}
